import { FC, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Icons
import { FaPlus, FaTrash, FaExclamation } from 'react-icons/fa';
// Types
import QuizTypes from 'types/QuizTypes';
import MediaFormats from 'types/MediaFormats';
// Models
import IQuiz from 'models/Quiz';
import IScoringResultBand from 'models/ScoringResultBand';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Components
import { Toggle, Input } from 'components/Controls';
import { Button, IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import ImageUpload from 'components/ImageUpload';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import Accordion from 'components/Accordion';
import { ColorPicker } from 'components/ColorPicker';
// Utilities
import { isRequired } from 'utilities/validation';

const MAX_BANDS = 5;
const MIN_BANDS = 2;

type Props = {
  defaultExpanded?: boolean;
}

const ScoringResult:FC<Props> = ({ defaultExpanded }) => {
  const { t } = useTranslation();

  const quiz:IQuiz | null = useAppSelector(selectQuiz);

  const { control, watch, getValues, formState: { errors } } = useFormContext();

  const [activeBandIndex, setActiveBandIndex] = useState(0);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'scoringResultPage.bands'
  });

  const pageEnabled = watch('scoringResultPage.enabled');
  const bands:IScoringResultBand[] = watch('scoringResultPage.bands') || [];

  const handleAddBand = () => {
    append({ ref: uuidv4(), minPercentage: 0, title: '', text: '' });
    setActiveBandIndex(fields.length);
  }

  const handleRemoveBand = (index:number) => () => {
    remove(index);
    setActiveBandIndex(prev => (prev >= index && prev > 0 ? prev - 1 : prev));
  }

  const hasMinPercentageValue = (band:IScoringResultBand) => band.minPercentage !== '' as any && band.minPercentage !== null && band.minPercentage !== undefined;

  const validateMinPercentage = (bandIndex:number) => (value:any) => {
    if (value === '' || value === null || value === undefined) return isRequired;
    const numValue = Number(value);
    if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) return t('pages.quizForm.bandMinPercentageOutOfRange');
    const allBands:IScoringResultBand[] = getValues('scoringResultPage.bands') || [];
    const isDuplicate = allBands.some((band, index) => index !== bandIndex && hasMinPercentageValue(band) && Number(band.minPercentage) === numValue);
    if (isDuplicate) return t('pages.quizForm.bandMinPercentageDuplicate');
    const hasZero = allBands.some(band => hasMinPercentageValue(band) && Number(band.minPercentage) === 0);
    if (!hasZero) return t('pages.quizForm.bandZeroRequired');
    return true;
  }

  const hasZeroBand = bands.some(band => hasMinPercentageValue(band) && Number(band.minPercentage) === 0);

  const getBandHasError = (index:number) => Boolean((errors.scoringResultPage?.bands as any)?.[index]);

  if ( quiz && ![QuizTypes.Scoring, QuizTypes.Streak].includes(quiz.type) ) return null;
  return (
    <div react-scroll="scoringResult">
      <Accordion
        label="Scoring Result"
        icon="cup"
        previewPage="scoring-result"
        defaultExpanded={defaultExpanded}
      >
        <div className="w-full">
          <div className="flex items-center mb-4">
            <span className="block text-white text-base font-semibold mr-3">{t('labels.showScreen')}</span>
            <Controller
              control={control} name="scoringResultPage.enabled"
              render={({ field: { onChange, value } }) => (
                <Toggle
                  id="scoringResultEnabled"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className="mb-2">
            <span className="block text-white text-base font-semibold mb-1">{t('labels.scoreBands')}</span>
            <span className="block text-gray-400 text-sm font-light leading-5 mb-4">
              {t('pages.quizForm.scoreBandsHint')}
            </span>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {fields.map((field, index) => (
                <Button
                  key={`score-band-tab-${field.id}`}
                  variant={index === activeBandIndex ? 'contained' : 'outlined'}
                  disabled={!pageEnabled}
                  onClick={() => setActiveBandIndex(index)}
                >
                  {`${t('labels.band')} ${index + 1}`}
                  {getBandHasError(index) ? (
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 bg-red-500 text-white text-[9px] rounded-full">
                      <FaExclamation />
                    </span>
                  ) : null}
                </Button>
              ))}
              {fields.length < MAX_BANDS ? (
                <Tooltip title={t('pages.quizForm.addBandButton')}>
                  <IconButton onClick={handleAddBand} disabled={!pageEnabled}>
                    <FaPlus />
                  </IconButton>
                </Tooltip>
              ) : null}
            </div>

            {pageEnabled && fields.length > 0 && !hasZeroBand ? (
              <span className="block text-red-500 text-sm py-1 mb-4">{t('pages.quizForm.bandZeroRequired')}</span>
            ) : null}

            {fields.map((field:any, index:number) => (
              <div
                className={`flex flex-wrap -mx-2 ${index === activeBandIndex ? '' : 'hidden'}`}
                key={`score-band-form-${field.id}`}
              >
                <div className="w-full sm:w-1/3 px-2">
                  <Controller
                    control={control}
                    name={`scoringResultPage.bands[${index}].minPercentage`}
                    rules={{ required: isRequired, validate: validateMinPercentage(index) }}
                    render={({ field: rhfField }) => (
                      <Input
                        {...rhfField}
                        id={`scoringResultPage.bands[${index}].minPercentage`}
                        type="number"
                        min={0}
                        max={100}
                        label={t('labels.minPercentage')}
                        required
                        disabled={!pageEnabled}
                        error={Boolean((errors.scoringResultPage?.bands as any)?.[index]?.minPercentage)}
                        errorText={(errors.scoringResultPage?.bands as any)?.[index]?.minPercentage?.message || ''}
                      />
                    )}
                  />
                </div>
                <div className="w-full sm:w-2/3 px-2">
                  <MultiLanguageTextField
                    name={`scoringResultPage.bands[${index}].title`}
                    label={t('labels.title')}
                    rules={{ required: isRequired }}
                    required
                    error={Boolean((errors.scoringResultPage?.bands as any)?.[index]?.title)}
                    errorText={(errors.scoringResultPage?.bands as any)?.[index]?.title?.message || ''}
                    disabled={!pageEnabled}
                    customKey={`scoringResultPage.bands[${field.ref}].title`}
                  />
                </div>
                <div className="w-full px-2 pt-4 sm:pt-0">
                  <MultiLanguageTextField
                    name={`scoringResultPage.bands[${index}].text`}
                    label={t('labels.text')}
                    disabled={!pageEnabled}
                    customKey={`scoringResultPage.bands[${field.ref}].text`}
                  />
                </div>
                <div className="w-full pt-4 sm:w-1/2 px-2">
                  <Controller
                    control={control}
                    name={`scoringResultPage.bands[${index}].ctaUrl`}
                    render={({ field: rhfField }) => (
                      <Input
                        {...rhfField}
                        id={`scoringResultPage.bands[${index}].ctaUrl`}
                        label={t('labels.ctaUrl')}
                        disabled={!pageEnabled}
                      />
                    )}
                  />
                </div>
                <div className="w-full sm:w-1/2 px-2 sm:pt-4">
                  <MultiLanguageTextField
                    name={`scoringResultPage.bands[${index}].ctaButtonTitle`}
                    label={t('labels.ctaButtonTitle')}
                    disabled={!pageEnabled}
                    customKey={`scoringResultPage.bands[${field.ref}].ctaButtonTitle`}
                  />
                </div>

                {fields.length > MIN_BANDS ? (
                  <div className="w-full px-2 text-right pt-4 sm:pt-0">
                    <Button variant="outlined" onClick={handleRemoveBand(index)} disabled={!pageEnabled}>
                      <FaTrash className="inline-block mr-2" />{t('pages.quizForm.removeBandButton')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="sm:w-1/3">
            <Controller
              control={control} name="scoringResultPage.image"
              render={({ field }) => (
                <ImageUpload
                  {...field}
                  format={MediaFormats.CTA}
                  label="labels.image"
                  disabled={!pageEnabled}
                />
              )}
            />
          </div>
          <div className="sm:w-1/3">
            <div className="mr-4 mb-4">
              <Controller
                control={control} name="scoringResultPage.primaryColor" rules={{ required: isRequired }}
                render={({ field: { value, onChange } }) => (
                  <ColorPicker
                    label={t('labels.primaryColor')}
                    value={value}
                    onChange={onChange}
                    error={Boolean(errors.scoringResultPage?.primaryColor)}
                    errorText={errors.scoringResultPage?.primaryColor ? errors.scoringResultPage.primaryColor.message : ''}
                    helperText=""
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  )
}

export default ScoringResult;
